import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Axios Response Interceptor - Handle 401 Unauthorized errors
axios.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  (error) => {
    // Check if error is 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // Check if we're trying to access an admin endpoint
      const isAdminEndpoint = error.config.url.includes('/admin/');
      
      if (isAdminEndpoint) {
        // Clear the expired token
        localStorage.removeItem('admin_token');
        
        // Redirect to admin login page
        // Check if we're not already on the login page to avoid infinite loop
        if (!window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login';
        }
      }
    }
    
    // Return the error to be handled by the calling function
    return Promise.reject(error);
  }
);

// Services
export const getServices = async () => {
  try {
    const response = await axios.get(`${API}/services`);
    return response.data;
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

// Gallery
export const getGallery = async (category = null) => {
  try {
    const url = category ? `${API}/gallery?category=${category}` : `${API}/gallery`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching gallery:', error);
    throw error;
  }
};

// Testimonials
export const getTestimonials = async () => {
  try {
    const response = await axios.get(`${API}/testimonials`);
    return response.data;
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    throw error;
  }
};

// Packages
export const getPackages = async () => {
  try {
    const response = await axios.get(`${API}/packages`);
    return response.data;
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw error;
  }
};

// Categories
export const getCategories = async () => {
  try {
    const response = await axios.get(`${API}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Rentals
export const getRentals = async () => {
  try {
    const response = await axios.get(`${API}/rentals`);
    return response.data;
  } catch (error) {
    console.error('Error fetching rentals:', error);
    throw error;
  }
};

// Quote Request
export const createQuote = async (quoteData) => {
  try {
    const response = await axios.post(`${API}/quotes`, quoteData);
    return response.data;
  } catch (error) {
    console.error('Error creating quote:', error);
    throw error;
  }
};

// Contact
export const createContact = async (contactData) => {
  try {
    const response = await axios.post(`${API}/contact`, contactData);
    return response.data;
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

// ============ ADMIN API ============

// Admin Login
export const adminLogin = async (username, password) => {
  try {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    if (response.data.success && response.data.token) {
      localStorage.setItem('admin_token', response.data.token);
    }
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

// Admin Logout
export const adminLogout = () => {
  localStorage.removeItem('admin_token');
};

// Check if admin is logged in
export const isAdminLoggedIn = () => {
  return !!localStorage.getItem('admin_token');
};

// Get Quotes (Admin)
export const getAdminQuotes = async (status = null) => {
  try {
    const url = status ? `${API}/admin/quotes?status=${status}` : `${API}/admin/quotes`;
    const response = await axios.get(url, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin quotes:', error);
    throw error;
  }
};

// Update Quote Status (Admin)
export const updateQuoteStatus = async (quoteId, status) => {
  try {
    const response = await axios.patch(
      `${API}/admin/quotes/${quoteId}`,
      { status },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating quote status:', error);
    throw error;
  }
};

// Delete Quote (Admin)
export const deleteQuote = async (quoteId) => {
  try {
    const response = await axios.delete(
      `${API}/admin/quotes/${quoteId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting quote:', error);
    throw error;
  }
};

// Get Contacts (Admin)
export const getAdminContacts = async () => {
  try {
    const response = await axios.get(`${API}/admin/contacts`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin contacts:', error);
    throw error;
  }
};

// Delete Contact (Admin)
export const deleteContact = async (contactId) => {
  try {
    const response = await axios.delete(
      `${API}/admin/contacts/${contactId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

const apiInstance = axios.create({ baseURL: API });
apiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isAdminEndpoint = error.config.url.includes('/admin/');
      if (isAdminEndpoint) {
        localStorage.removeItem('admin_token');
        if (!window.location.pathname.includes('/admin/login')) window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);
export default apiInstance;
